import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';

const EMPTY_FORM = {
  accountName: '',
  password: '',
  passwordConfirm: '',
  email: '',
  phone: '',
  isActive: true,
  permissions: []
};

function getCurrentUserIdFromToken() {
  try {
    const token = localStorage.getItem('callnote_token');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const normalizedPayload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(paddedPayload));
    return Number(payload?.sub) || null;
  } catch (_error) {
    return null;
  }
}

function normalizeAccount(item) {
  return {
    id: item.id,
    accountName: item.account_name || item.accountName || '',
    email: item.email || '',
    phone: item.phone || '',
    isActive: Boolean(item.is_active ?? item.isActive),
    permissions: Array.isArray(item.permissions) ? item.permissions : []
  };
}

function PermissionChecklist({ availablePermissions, selectedPermissions, onToggle, disabled = false, disabledHint = '' }) {
  return (
    <div className="permissions-block">
      <div className="section-subtitle">Права доступа</div>
      {availablePermissions.map((permission) => (
        <label key={permission.code} className="checkbox-row">
          <input
            type="checkbox"
            checked={selectedPermissions.includes(permission.code)}
            onChange={() => onToggle(permission.code)}
            disabled={disabled}
          />
          <span>{permission.title} <small className="muted">({permission.code})</small></span>
        </label>
      ))}
      {!availablePermissions.length ? <div className="muted">Права пока не загружены</div> : null}
      {disabled && disabledHint ? <div className="muted">{disabledHint}</div> : null}
    </div>
  );
}

export function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const currentUserId = useMemo(() => getCurrentUserIdFromToken(), []);

  const editingAccountName = useMemo(() => {
    if (!editForm?.id) return '';
    return accounts.find((item) => item.id === editForm.id)?.accountName || '';
  }, [accounts, editForm]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [accountsData, permissionsData] = await Promise.all([
        api('/accounts'),
        api('/accounts/meta/permissions')
      ]);
      setAccounts((Array.isArray(accountsData) ? accountsData : []).map(normalizeAccount));
      setAvailablePermissions(Array.isArray(permissionsData) ? permissionsData : []);
    } catch (currentError) {
      setError(currentError.message || 'Не удалось загрузить аккаунты');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function togglePermission(target, setter, code) {
    setter((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(code)
        ? prev.permissions.filter((item) => item !== code)
        : [...prev.permissions, code]
    }));
  }

  function startEdit(account) {
    setEditForm({
      ...account,
      permissions: [...account.permissions],
      password: '',
      passwordConfirm: ''
    });
    setMessage('');
    setError('');
  }

  function cancelEdit() {
    setEditForm(null);
    setForm({ ...EMPTY_FORM, permissions: [] });
    setMessage('');
    setError('');
  }

  function resetCreateForm() {
    setForm({ ...EMPTY_FORM, permissions: [] });
    setEditForm(null);
    setMessage('');
    setError('');
  }

  async function create() {
    if (!String(form.password || '').trim()) {
      setError('Введите пароль');
      return;
    }

    if (form.password !== form.passwordConfirm) {
      setError('Пароль и подтверждение пароля не совпадают');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api('/accounts', { method: 'POST', body: JSON.stringify(form) });
      resetCreateForm();
      await load();
      setMessage('Аккаунт создан');
    } catch (currentError) {
      setError(currentError.message || 'Не удалось создать аккаунт');
    } finally {
      setSaving(false);
    }
  }

  async function saveEdit() {
    if (!editForm?.id) return;

    if (String(editForm.password || '').trim() || String(editForm.passwordConfirm || '').trim()) {
      if (!String(editForm.password || '').trim()) {
        setError('Введите новый пароль');
        return;
      }
      if (editForm.password !== editForm.passwordConfirm) {
        setError('Новый пароль и подтверждение не совпадают');
        return;
      }
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api(`/accounts/${editForm.id}`, { method: 'PUT', body: JSON.stringify(editForm) });
      await load();
      setMessage('Аккаунт обновлён');
      cancelEdit();
    } catch (currentError) {
      setError(currentError.message || 'Не удалось обновить аккаунт');
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (currentUserId && Number(id) === Number(currentUserId)) {
      setError('Нельзя удалить собственный аккаунт');
      return;
    }
    if (!window.confirm('Удалить аккаунт?')) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api(`/accounts/${id}`, { method: 'DELETE' });
      await load();
      setMessage('Аккаунт удалён');
    } catch (currentError) {
      setError(currentError.message || 'Не удалось удалить аккаунт');
    } finally {
      setSaving(false);
    }

    if (editForm?.id === id) {
      resetCreateForm();
    }
  }

  return (
    <div className="page-stack">
      {message ? <section className="card success-banner">{message}</section> : null}
      {error ? <section className="card error-banner">{error}</section> : null}

      <div className="grid two-columns accounts-layout">
        <section className="card">
          <div className="section-header">
            <h2>{editForm ? `Редактировать аккаунт: ${editingAccountName || `#${editForm.id}`}` : 'Создать аккаунт'}</h2>
            {editForm ? (
              <button type="button" className="link-button" onClick={cancelEdit} disabled={saving}>Отмена</button>
            ) : null}
          </div>

          <label>
            Account name
            <input
              value={editForm ? editForm.accountName : form.accountName}
              onChange={(e) => editForm
                ? setEditForm({ ...editForm, accountName: e.target.value })
                : setForm({ ...form, accountName: e.target.value })}
            />
          </label>

          {!editForm ? (
            <>
              <label>
                Password
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </label>
              <label>
                Подтверждение пароля
                <input type="password" value={form.passwordConfirm} onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })} />
              </label>
            </>
          ) : (
            <>
              <label>
                Новый пароль
                <input type="password" value={editForm.password || ''} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
              </label>
              <label>
                Подтверждение нового пароля
                <input type="password" value={editForm.passwordConfirm || ''} onChange={(e) => setEditForm({ ...editForm, passwordConfirm: e.target.value })} />
              </label>
            </>
          )}

          <label>Email<input value={editForm ? editForm.email : form.email} onChange={(e) => editForm ? setEditForm({ ...editForm, email: e.target.value }) : setForm({ ...form, email: e.target.value })} /></label>
          <label>Телефон<input value={editForm ? editForm.phone : form.phone} onChange={(e) => editForm ? setEditForm({ ...editForm, phone: e.target.value }) : setForm({ ...form, phone: e.target.value })} /></label>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={editForm ? editForm.isActive : form.isActive}
              disabled={Boolean(editForm && currentUserId && Number(editForm.id) === Number(currentUserId))}
              onChange={(e) => editForm
                ? setEditForm({ ...editForm, isActive: e.target.checked })
                : setForm({ ...form, isActive: e.target.checked })}
            />
            Аккаунт активен
          </label>
          {editForm && currentUserId && Number(editForm.id) === Number(currentUserId)
            ? <div className="muted">Собственный аккаунт нельзя деактивировать из интерфейса.</div>
            : null}

          <PermissionChecklist
            availablePermissions={availablePermissions}
            selectedPermissions={editForm ? editForm.permissions : form.permissions}
            onToggle={(code) => editForm
              ? togglePermission(editForm, setEditForm, code)
              : togglePermission(form, setForm, code)}
            disabled={Boolean(editForm && currentUserId && Number(editForm.id) === Number(currentUserId))}
            disabledHint={editForm && currentUserId && Number(editForm.id) === Number(currentUserId)
              ? 'Собственные права доступа меняются отдельно, чтобы не потерять доступ к админке.'
              : ''}
          />

          <div className="inline-actions top-gap">
            <button type="button" onClick={editForm ? saveEdit : create} disabled={saving}>
              {saving ? 'Сохранение...' : (editForm ? 'Сохранить изменения' : 'Создать')}
            </button>
            {editForm ? <button type="button" className="link-button" onClick={cancelEdit} disabled={saving}>Отмена</button> : null}
          </div>
        </section>

        <section className="card">
          <div className="section-header">
            <h2>Список аккаунтов</h2>
            {loading ? <div className="muted">Загрузка...</div> : null}
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>ID</th><th>Account</th><th>Email</th><th>Телефон</th><th>Статус</th><th>Права</th><th></th></tr></thead>
              <tbody>
                {accounts.map((item) => (
                  <tr key={item.id} className={editForm?.id === item.id ? 'selected-row' : ''}>
                    <td>{item.id}</td>
                    <td>{item.accountName}</td>
                    <td>{item.email || '-'}</td>
                    <td>{item.phone || '-'}</td>
                    <td>{item.isActive ? 'Активен' : 'Выключен'}</td>
                    <td>{item.permissions?.length ? item.permissions.join(', ') : '-'}</td>
                    <td>
                      <div className="inline-actions">
                        <button type="button" className="action-button" onClick={() => startEdit(item)} disabled={saving}>Изменить</button>
                        <button type="button" className="action-button danger" onClick={() => remove(item.id)} disabled={saving || (currentUserId && Number(item.id) === Number(currentUserId))}>Удалить</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!accounts.length && !loading ? <tr><td colSpan="7">Аккаунтов пока нет</td></tr> : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
